import { Bundle } from '@lune-climate/lune'
import { chartColorsSmall } from '@lune-fe/lune-components-lib'
import {
    ButtonGroupNew,
    IconButtonDropdownNew,
    LuneThemeNew,
    useIsMobile,
} from '@lune-fe/lune-ui-lib'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined'
import Box from '@mui/material/Box'
import { Big } from 'big.js'
import { FC, useCallback, useMemo, useRef } from 'react'
import { Line } from 'react-chartjs-2'
import { Range } from 'react-date-range'

import useAccounts from 'hooks/useAccounts'
import useUserState from 'hooks/useUserState'
import { getCurrencySymbol } from 'models/currency'
import { OrderType } from 'models/order'
import { downloadImageFromHtml } from 'utils'
import { getGradient } from 'views/Dashboard/Charts/chartUtils'
import {
    BundleData,
    chartOptions,
    Dataset,
    getDateLabels,
} from 'views/Dashboard/Charts/OrdersChartConfig'
import Totals from 'views/Dashboard/Totals'

import 'chart.js/auto'
import 'chartjs-adapter-moment'

const OrdersChart: FC<{
    analyticsType: OrderType
    data: BundleData[]
    dateRange: Range
    allBundles: Bundle[]
    setAnalyticsType: (type: OrderType) => void
}> = ({ analyticsType, data, dateRange, allBundles, setAnalyticsType }) => {
    const { userState } = useUserState()
    const { palette } = LuneThemeNew
    const { activeAccount } = useAccounts()
    const { isMobile } = useIsMobile()
    const printRef = useRef<HTMLElement>(null)

    const getBundleColor = useCallback(
        (bundle: BundleData) => {
            const b = allBundles.find((b) => b.id === bundle.bundleId)
            return b ? b.backgroundColour : palette.Grey600
        },
        [allBundles, palette.Grey600],
    )

    const chartData = useMemo(() => {
        const valueOrQuantityData = (data: Dataset[]) => {
            return Array.from(
                data.map((d) => ({
                    x: d.date,
                    y: parseFloat(
                        Big(analyticsType === OrderType.VALUE ? d.value : d.quantity).toFixed(2),
                    ).toString(),
                })),
            )
        }

        return {
            labels: getDateLabels(data),
            datasets: data.map((bundle, index) => {
                const color = chartColorsSmall[index] || getBundleColor(bundle) || '#000000'
                return {
                    label: bundle.bundleName,
                    data: valueOrQuantityData(bundle.timeseries),
                    fill: true,
                    borderColor: color,
                    backgroundColor: (context: any) => {
                        const chart = context.chart
                        const { ctx, chartArea } = chart

                        if (!chartArea) {
                            // This case happens on initial chart load
                            return
                        }
                        return getGradient(ctx, chartArea, color)
                    },
                    cubicInterpolationMode: 'monotone' as const,
                    tension: 0.8,
                    pointStyle: 'circle' as const,
                    pointBackgroundColor: color,
                    pointBorderColor: color,
                }
            }),
        }
    }, [analyticsType, data, getBundleColor])

    const currencySymbol = getCurrencySymbol(userState?.account.currency || `GBP`)

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                    mb: isMobile ? 2 : -5,
                }}
            >
                <ButtonGroupNew
                    items={[
                        {
                            label: 'tCO₂',
                            value: OrderType.QUANTITY,
                            dataTestId: 'by-volume-btn',
                        },
                        {
                            label: currencySymbol,
                            value: OrderType.VALUE,
                            dataTestId: 'by-value-btn',
                        },
                    ]}
                    onChange={(type) => {
                        setAnalyticsType(type as OrderType)
                    }}
                    value={analyticsType}
                />
                <IconButtonDropdownNew menuAutoWidth icon={<MoreVertOutlinedIcon />}>
                    <IconButtonDropdownNew.MenuItem
                        title={'Download graph'}
                        onClick={() => {
                            if (printRef.current) {
                                downloadImageFromHtml(printRef.current, `orders-graph`)
                            }
                        }}
                        icon={<FileDownloadOutlinedIcon />}
                    />
                </IconButtonDropdownNew>
            </Box>
            <Box ref={printRef}>
                <Totals data={data} analyticsType={analyticsType} />
                <Box
                    sx={{
                        position: 'relative',
                        height: '450px',
                        width: '100%',
                        mt: 2,
                    }}
                >
                    <Box sx={{ height: '100%', wight: '100%' }}>
                        <Line
                            options={
                                chartOptions(
                                    analyticsType,
                                    chartData,
                                    analyticsType === OrderType.QUANTITY ? 'tCO' : currencySymbol,
                                    activeAccount?.currency,
                                    dateRange,
                                ) as any
                            }
                            data={chartData}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default OrdersChart
